import { useEffect, useMemo, useState, Fragment } from "react";
import { useLang } from "../../contexts/LanguageContext";
import PageHero from "../../components/public/PageHero";
import { Link } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import { api } from "../../lib/api";
import { DEFAULT_BENEFITS } from "../../data/benefitsDefault";

// Renders a single cell (yes / no / localised text) with the right colours.
function Cell({ value, lang, featured }) {
  if (value?.type === "yes") {
    return (
      <Check
        className={`w-5 h-5 mx-auto ${featured ? "text-cecit-gold" : "text-[#0F2040]"}`}
        strokeWidth={2.5}
      />
    );
  }
  if (value?.type === "no" || !value) {
    return (
      <Minus className={`w-4 h-4 mx-auto ${featured ? "text-white/50" : "text-[#0F2040]/40"}`} strokeWidth={2} />
    );
  }
  const text = value[lang] || value.es || value.fr || "";
  return <span className={`text-sm font-medium ${featured ? "text-white" : "text-[#0F2040]"}`}>{text}</span>;
}

export default function VentajasSocio() {
  const { t, lang } = useLang();
  const [data, setData] = useState(DEFAULT_BENEFITS);

  useEffect(() => {
    api.get("/settings")
      .then((r) => {
        const b = r.data?.benefits_data;
        if (b && Array.isArray(b.tiers) && Array.isArray(b.categories) && b.tiers.length === 3) {
          setData(b);
        }
      })
      .catch(() => {});
  }, []);

  const tiers = useMemo(() => data.tiers.slice(0, 3), [data]);

  return (
    <div data-testid="ventajas-page">
      <PageHero
        title={t("nav.ventajas")}
        subtitle={lang === "es"
          ? "Tres modalidades de socio, un mismo objetivo: acompañarle en su crecimiento entre España y Marruecos."
          : "Trois modalités d'adhésion, un même objectif : vous accompagner dans votre croissance entre l'Espagne et le Maroc."}
      />

      {/* Tier cards */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-4">
        <div className="grid md:grid-cols-3 gap-px bg-gray-200" data-testid="ventajas-tiers">
          {tiers.map((tier) => (
            <div
              key={tier.key}
              data-testid={`tier-${tier.key}`}
              className={`p-10 flex flex-col ${tier.featured ? "bg-[#0F2040] text-white" : "bg-white"}`}
            >
              <p className={`text-xs font-semibold uppercase tracking-[0.22em] mb-3 ${tier.featured ? "text-cecit-gold" : "text-[#C8102E]"}`}>
                {tier.featured ? "★ " : ""}{tier[`name_${lang}`] || tier.name_es}
              </p>
              <h3 className={`font-serif text-4xl mb-1 ${tier.featured ? "text-white" : "text-[#0F2040]"}`}>{tier.price}</h3>
              <p className={`text-xs uppercase tracking-widest mb-6 ${tier.featured ? "text-white/70" : "text-gray-500"}`}>
                {lang === "es" ? "Cuota anual" : "Cotisation annuelle"}
              </p>
              <Link
                to="/contacto"
                className={`inline-block w-full text-center py-3.5 text-sm font-medium tracking-wide transition-colors mt-auto ${
                  tier.featured
                    ? "bg-[#C8102E] hover:bg-[#A30D25] text-white"
                    : "border border-[#0F2040] text-[#0F2040] hover:bg-[#0F2040] hover:text-white"
                }`}
                data-testid={`tier-cta-${tier.key}`}
              >
                {t("nav.becomeMember")}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison table */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C8102E] mb-3">
            {lang === "es" ? "Comparativa detallada" : "Comparatif détaillé"}
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#0F2040]">
            {lang === "es" ? "Ventajas por modalidad de socio" : "Avantages par modalité d'adhésion"}
          </h2>
        </div>

        <div className="overflow-x-auto -mx-6 px-6" data-testid="ventajas-table-wrapper">
          <table className="w-full min-w-[900px] border-collapse" data-testid="ventajas-table">
            <thead>
              <tr>
                <th className="bg-white text-[#0F2040] text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider w-2/5 border-b border-gray-200">
                  {lang === "es" ? "Modalidad de socio" : "Modalité d'adhésion"}
                </th>
                {tiers.map((tier) => (
                  <th
                    key={tier.key}
                    className={`text-center px-4 py-4 w-1/5 border-b border-gray-200 ${tier.featured ? "bg-[#0F2040] text-white" : "bg-white text-[#0F2040]"}`}
                  >
                    <div className="text-xs uppercase tracking-widest font-semibold">
                      {tier[`name_${lang}`] || tier.name_es}
                    </div>
                    <div className={`text-sm mt-1 font-serif ${tier.featured ? "text-cecit-gold" : "text-[#0F2040]"}`}>{tier.price}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.categories.map((cat) => (
                <Fragment key={cat.key}>
                  <tr data-testid={`ventajas-cat-${cat.key}`}>
                    <td colSpan={4} className="bg-[#F8D6DB] text-[#0F2040] font-semibold px-6 py-2.5 text-sm uppercase tracking-wider border-b border-white">
                      {cat[`label_${lang}`] || cat.label_es}
                    </td>
                  </tr>
                  {cat.rows.map((row, ri) => (
                    <tr key={`${cat.key}-${ri}`} className="border-b border-white">
                      <td className="bg-[#FCEAED] text-[#0F2040] px-6 py-2.5 text-sm leading-snug">
                        {row[`label_${lang}`] || row.label_es}
                      </td>
                      {row.cells.slice(0, 3).map((cell, ci) => (
                        <td
                          key={ci}
                          className={`text-center px-4 py-2.5 border-b ${tiers[ci]?.featured ? "bg-[#0F2040] border-white/10" : "bg-white border-gray-100"}`}
                        >
                          <Cell value={cell} lang={lang} featured={tiers[ci]?.featured} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-gray-500 text-center mt-6">
          {lang === "es"
            ? "Los precios están expresados en dírhams marroquíes (MAD) e incluyen la cuota anual de socio."
            : "Les prix sont exprimés en dirhams marocains (MAD) et incluent la cotisation annuelle de membre."}
        </p>
      </section>

      <section className="bg-[#0F2040] text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="font-serif text-3xl md:text-4xl mb-4">
            {lang === "es" ? "¿Preparado para unirse a la CECIT?" : "Prêt à rejoindre la CECIT ?"}
          </h3>
          <p className="text-white/75 mb-8 max-w-2xl mx-auto">
            {lang === "es"
              ? "Nuestro equipo le orientará hacia la modalidad de socio que mejor se adapte a los objetivos de su empresa."
              : "Notre équipe vous orientera vers la modalité d'adhésion qui correspond le mieux aux objectifs de votre entreprise."}
          </p>
          <Link
            to="/contacto"
            data-testid="ventajas-contact-cta"
            className="inline-flex items-center bg-[#C8102E] hover:bg-[#A30D25] text-white px-8 py-4 text-sm font-medium tracking-wide transition-colors"
          >
            {lang === "es" ? "Contactar con la Cámara" : "Contacter la Chambre"}
          </Link>
        </div>
      </section>
    </div>
  );
}
